/** @jsxImportSource @emotion/react */
import {
  Button,
  CryptoCurrency,
  Icon,
  ListItem,
  Tooltip,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useNavigate } from 'react-router-dom';
import { ContactStatus, Recipient } from 'api/walletRecipientApi.types';

interface Props {
  recipient: Recipient;
  showActionButtons: boolean;
  approveRecipientLoading: boolean;
  approveRecipientFn: (params: { contactId: string }) => Promise<unknown>;
  removeRecipientLoading: boolean;
  setDeleteContactId: (contactId: string) => void;
}

export const RecipientListItem = ({
  recipient,
  showActionButtons,
  approveRecipientLoading,
  approveRecipientFn,
  removeRecipientLoading,
  setDeleteContactId,
}: Props) => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  return (
    <ListItem
      icon={recipient.currencyCode as CryptoCurrency}
      title={recipient.label}
      subtitle={
        <div
          css={{
            display: 'flex',
            gap: `calc(${spacing.xxxs}/2)`,
          }}
        >
          <div>
            <Typography variant="bodySmallestBold">
              Address:
              <br /> Created by:
              {recipient.approvedBy && (
                <>
                  <br /> Approved by:
                </>
              )}
            </Typography>
          </div>
          <div>
            <Typography variant="bodySmallest">
              {recipient.address}
              <br /> {recipient.createdByEmail ?? recipient.createdBy}
              <br />
              {recipient.approvedBy && recipient.approvedByEmail}
            </Typography>
          </div>
        </div>
      }
      key={recipient.address}
      action={
        showActionButtons &&
        recipient.canApprove &&
        recipient.approvedBy === null ? (
          approveRecipientLoading ? (
            <Icon name="spinner" />
          ) : (
            <Tooltip title="Approve Recipient">
              <Button
                variant="icon"
                onClick={() =>
                  approveRecipientFn({
                    contactId: recipient.uuid,
                  })
                }
                icon="checkmark"
              />
            </Tooltip>
          )
        ) : undefined
      }
      content={
        showActionButtons ? (
          removeRecipientLoading ? (
            <Icon name="spinner" />
          ) : (
            <div css={{ display: 'flex', gap: spacing.xxxs }}>
              {!recipient.isInternal &&
                (recipient.status === ContactStatus.PENDING_TRAVEL_RULE ||
                  recipient.status ===
                    ContactStatus.PENDING_PROOF_OF_OWNERSHIP) && (
                  <Tooltip title="Add travel rule info">
                    <div>
                      <Button
                        variant="icon"
                        icon="info"
                        onClick={() => {
                          void navigate(
                            `add/travel-rule-info/${recipient.uuid}${
                              recipient.status ===
                              ContactStatus.PENDING_PROOF_OF_OWNERSHIP
                                ? '?showProofOfOwnership=true'
                                : ''
                            }`
                          );
                        }}
                      />
                    </div>
                  </Tooltip>
                )}
              <Tooltip title="Delete Recipient">
                <div css={{ svg: { color: palette.danger.default } }}>
                  <Button
                    variant="icon"
                    icon="trash"
                    onClick={() => {
                      setDeleteContactId(recipient.uuid);
                    }}
                  />
                </div>
              </Tooltip>
            </div>
          )
        ) : undefined
      }
    />
  );
};
